import React, { useState, useRef } from 'react';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import Pricing from './components/Pricing';
import Chatbot from './components/Chatbot';
import AdminPanel from './components/AdminPanel';
import BillingHistory from './components/BillingHistory';
import InstallPrompt from './components/InstallPrompt';
import './components/Pricing.css';
import './components/Chatbot.css';
import './components/AdminPanel.css';
import './components/BillingHistory.css';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  FileText,
  Upload,
  Send,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  Image,
  FileAudio,
  FileVideo,
  User,
  ClipboardList,
  Activity,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Download,
  Heart,
  Eye,
  Trash2,
  Cpu,
  Zap,
  Menu,
  XCircle,
  BookOpen,
  ExternalLink,
  FileSearch,
  LogOut,
  Building2,
  CreditCard,
  History,
  TrendingUp,
  MessageCircle,
  Settings
} from 'lucide-react';
import './App.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:8080/api');

const INPUT_MODES = [
  { id: 'text', label: 'Text', icon: FileText },
  { id: 'audio', label: 'Audio', icon: Mic },
  { id: 'video', label: 'Video', icon: Video },
];

const AI_MODELS = [
  { 
    id: 'gpt4o', 
    name: 'Opinion 1', 
    provider: 'AI Engine A',
    color: '#10a37f',
    description: 'Advanced multimodal analysis'
  },
  { 
    id: 'gemini', 
    name: 'Opinion 2', 
    provider: 'AI Engine B',
    color: '#4285f4',
    description: 'Fast & accurate diagnosis'
  },
  { 
    id: 'claude', 
    name: 'Opinion 3', 
    provider: 'AI Engine C',
    color: '#d97706',
    description: 'Detailed medical analysis'
  },
];

function DiagnosisApp() {
  const { currentUser, userProfile, logout, isSuperAdmin, saveDiagnosis } = useAuth();
  const [inputMode, setInputMode] = useState('text');
  const [symptoms, setSymptoms] = useState('');
  const [patientInfo, setPatientInfo] = useState({
    age: '',
    gender: '',
    weight: '',
    height: '',
    bloodType: '',
    allergies: '',
  });
  const [medicalHistory, setMedicalHistory] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [diagnosisResults, setDiagnosisResults] = useState(null);
  const [consensus, setConsensus] = useState(null);
  const [references, setReferences] = useState([]);
  const [showReferences, setShowReferences] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPatientInfo, setShowPatientInfo] = useState(false);
  const [selectedModels, setSelectedModels] = useState(['gpt4o']);
  const [activeResultTab, setActiveResultTab] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [chatMode, setChatMode] = useState(null); // null or modelId ('gpt4o', 'gemini', 'claude')
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins.toString().padStart(2, '0') + ':' + secs.toString().padStart(2, '0');
  };

  const toggleModel = (modelId) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== modelId);
      }
      return [...prev, modelId];
    });
  };

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError('Could not access microphone. Please ensure permissions are granted.');
    }
  };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' }, 
        audio: true 
      });
      streamRef.current = stream;
      
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }

      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setVideoBlob(blob);
        setVideoPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError('Could not access camera/microphone. Please ensure permissions are granted.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      type: file.type,
      size: file.size,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (id) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
    setFileDescriptions(prev => {
      const newDesc = { ...prev };
      delete newDesc[id];
      return newDesc;
    });
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('audio/')) return FileAudio;
    if (type.startsWith('video/')) return FileVideo;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if user has enough credits
  const checkCredits = () => {
    console.log('=== Credit Check ===');
    console.log('isSuperAdmin:', isSuperAdmin);
    console.log('currentUser:', currentUser?.phoneNumber);
    console.log('userProfile:', userProfile);
    console.log('selectedModels:', selectedModels.length);
    
    // Super admin always has credits
    if (isSuperAdmin) {
      console.log('✓ Super admin - unlimited credits');
      return true;
    }
    
    // If no profile loaded yet, allow for new users with default 5 credits
    if (!userProfile) {
      console.log('No profile - checking if new user');
      return selectedModels.length <= 5; // Default 5 credits for new users
    }
    
    // Check organization credits
    const org = userProfile.organization;
    let creditsTotal, creditsUsed, creditsRemaining;
    
    if (org) {
      creditsTotal = org.creditsTotal ?? 5;
      creditsUsed = org.creditsUsed ?? 0;
    } else {
      // Fallback to userProfile level
      creditsTotal = userProfile.creditsTotal ?? 5;
      creditsUsed = userProfile.creditsUsed ?? 0;
    }
    
    creditsRemaining = creditsTotal - creditsUsed;
    console.log('Credits:', { creditsTotal, creditsUsed, creditsRemaining, needed: selectedModels.length });
    
    const hasEnough = creditsRemaining >= selectedModels.length;
    console.log('Has enough credits:', hasEnough);
    return hasEnough;
  };

  const submitDiagnosis = async () => {
    // Check credits first
    if (!checkCredits()) {
      setError('Insufficient credits. Please upgrade your plan or select fewer models.');
      return;
    }

    setLoading(true);
    setError(null);
    setDiagnosisResults(null);
    setConsensus(null);
    setReferences([]);
    setShowReferences(false);
    setTranscription('');
    setActiveResultTab(0);

    try {
      const formData = new FormData();
      
      formData.append('patientInfo', JSON.stringify(patientInfo));
      formData.append('medicalHistory', medicalHistory);
      formData.append('symptoms', symptoms);
      formData.append('selectedModels', JSON.stringify(selectedModels));
      
      // Add user info for tracking
      if (currentUser) {
        formData.append('userId', currentUser.uid);
        formData.append('userEmail', currentUser.email);
      }

      const descriptions = uploadedFiles.map(f => fileDescriptions[f.id] || f.name);
      formData.append('fileDescriptions', JSON.stringify(descriptions));

      uploadedFiles.forEach(f => {
        formData.append('files', f.file);
      });

      if ((inputMode === 'audio' && audioBlob) || (inputMode === 'video' && videoBlob)) {
        const mediaBlob = inputMode === 'audio' ? audioBlob : videoBlob;
        const transcribeForm = new FormData();
        transcribeForm.append('audio', mediaBlob, 'recording.webm');
        
        try {
          const transcribeRes = await axios.post(API_BASE_URL + '/transcribe', transcribeForm);
          if (transcribeRes.data.transcription) {
            setTranscription(transcribeRes.data.transcription);
            formData.append('audioTranscription', transcribeRes.data.transcription);
          }
        } catch (transcribeErr) {
          console.log('Transcription failed, continuing with text only');
        }
      }

      const response = await axios.post(API_BASE_URL + '/diagnose/multi', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'X-User-Id': currentUser?.uid || '',
          'X-User-Phone': currentUser?.phoneNumber || ''
        },
        timeout: 180000,
      });

      if (response.data.success) {
        setDiagnosisResults(response.data.results);
        setConsensus(response.data.consensus);
        setReferences(response.data.references || []);
        
        // Show credits used notification if returned
        if (response.data.creditsUsed) {
          console.log('Credits used for diagnosis:', response.data.creditsUsed);
        }

        // Save diagnosis to database and track credits
        try {
          await saveDiagnosis({
            patientInfo: patientInfo,
            symptoms: symptoms,
            medicalHistory: medicalHistory,
            results: response.data.results,
            consensus: response.data.consensus,
            references: response.data.references || [],
            filesCount: uploadedFiles.length
          }, selectedModels);
        } catch (saveErr) {
          console.error('Failed to save diagnosis:', saveErr);
          // Don't show error to user, diagnosis was still successful
        }
      } else {
        throw new Error(response.data.error || 'Failed to generate diagnosis');
      }
    } catch (err) {
      console.error('Diagnosis error:', err);
      // Check for insufficient credits error (HTTP 402)
      if (err.response?.status === 402) {
        setError(`⚠️ ${err.response.data.error}\n\nYou have ${err.response.data.creditsRemaining} credits remaining.`);
        // Optionally open pricing modal
        // setShowPricing(true);
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to connect to the server');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSymptoms('');
    setDiagnosisResults(null);
    setConsensus(null);
    setReferences([]);
    setShowReferences(false);
    setTranscription('');
    setError(null);
    setAudioBlob(null);
    setVideoBlob(null);
    setVideoPreviewUrl(null);
    setUploadedFiles([]);
    setFileDescriptions({});
    setActiveResultTab(0);
  };

  const canSubmit = () => {
    if (loading) return false;
    if (selectedModels.length === 0) return false;
    if (inputMode === 'text' && !symptoms.trim() && uploadedFiles.length === 0) return false;
    if (inputMode === 'audio' && !audioBlob && !symptoms.trim()) return false;
    if (inputMode === 'video' && !videoBlob && !symptoms.trim()) return false;
    return true;
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const renderDiagnosisContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h3 key={i} className="diagnosis-h3">{line.replace('## ', '')}</h3>;
      }
      if (line.startsWith('### ')) {
        return <h4 key={i} className="diagnosis-h4">{line.replace('### ', '')}</h4>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h4 key={i} className="diagnosis-h4">{line.replace(/\*\*/g, '')}</h4>;
      }
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <li key={i}>{line.replace(/^[-*] /, '')}</li>;
      }
      if (line.match(/^\d+\./)) {
        return <li key={i}>{line.replace(/^\d+\.\s*/, '')}</li>;
      }
      if (line.trim() === '') {
        return <br key={i} />;
      }
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i}>
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.replace(/\*\*/g, '')}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  // Calculate credits remaining
  const getCreditsRemaining = () => {
    if (isSuperAdmin) return '∞';
    
    if (!userProfile) return 5; // Default for new users
    
    const org = userProfile.organization;
    if (org) {
      return (org.creditsTotal ?? 5) - (org.creditsUsed ?? 0);
    }
    
    return (userProfile.creditsTotal ?? 5) - (userProfile.creditsUsed ?? 0);
  };
  
  const creditsRemaining = getCreditsRemaining();

  // Show admin panel
  if (showAdminPanel && isSuperAdmin) {
    return (
      <AdminPanel 
        onClose={() => setShowAdminPanel(false)}
        currentUser={currentUser}
      />
    );
  }

  // Show billing history
  if (showBilling) {
    return (
      <BillingHistory 
        onClose={() => setShowBilling(false)}
      />
    );
  }

  // Show pricing page
  if (showPricing) {
    return (
      <Pricing 
        onClose={() => setShowPricing(false)} 
        onPaymentSuccess={(result) => {
          console.log('Payment successful:', result);
          // Credits are automatically updated via refreshProfile in Pricing component
        }}
      />
    );
  }

  // Show chatbot
  if (chatMode) {
    return (
      <Chatbot 
        modelId={chatMode}
        onClose={() => setChatMode(null)}
      />
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src="/logo.png" alt="LatDiagnostics" className="logo-image" />
            <div className="logo-text">
              <h1>LatDiagnostics</h1>
              <span>AI-Powered Clinical Decision Support</span>
            </div>
          </div>
          
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <div className={'header-right ' + (mobileMenuOpen ? 'mobile-open' : '')}>
            <div className="header-badges">
              <span className="badge badge-secure">
                <CheckCircle size={14} />
                DISHA Compliant
              </span>
              {isSuperAdmin ? (
                <span className="badge badge-superadmin">
                  <Zap size={14} />
                  Super Admin
                </span>
              ) : (
                <button className="badge badge-credits" onClick={() => setShowPricing(true)}>
                  <CreditCard size={14} />
                  {creditsRemaining} Credits
                  <TrendingUp size={12} />
                </button>
              )}
            </div>
            
            <div className="user-menu-container">
              <button 
                className="user-menu-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  {userProfile?.name?.charAt(0)?.toUpperCase() || currentUser?.phoneNumber?.slice(-2) || 'U'}
                </div>
                <div className="user-info">
                  <span className="user-name">{userProfile?.name || currentUser?.phoneNumber || 'User'}</span>
                  <span className="user-org">{userProfile?.organizationName || ''}</span>
                </div>
                <ChevronDown size={16} />
              </button>
              
              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <Building2 size={16} />
                    <div>
                      <p className="dropdown-org">{userProfile?.organizationName || 'My Clinic'}</p>
                      <p className="dropdown-email">{userProfile?.phone || currentUser?.phoneNumber || ''}</p>
                    </div>
                  </div>
                  <div className="dropdown-stats">
                    <div className="stat-item">
                      <History size={14} />
                      <span>{userProfile?.diagnosesCount || 0} total diagnoses</span>
                    </div>
                    <div className="stat-item">
                      <Activity size={14} />
                      <span>{creditsRemaining} credits remaining</span>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  {isSuperAdmin && (
                    <button className="dropdown-item admin-btn" onClick={() => { setShowAdminPanel(true); setShowUserMenu(false); }}>
                      <Settings size={16} />
                      Admin Panel
                    </button>
                  )}
                  <button className="dropdown-item" onClick={() => { setShowBilling(true); setShowUserMenu(false); }}>
                    <FileText size={16} />
                    Billing & Invoices
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowPricing(true); setShowUserMenu(false); }}>
                    <TrendingUp size={16} />
                    Upgrade Plan
                  </button>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="disclaimer-banner">
        <AlertCircle size={16} />
        <p>
          <strong>Clinical Tool Only:</strong> AI suggestions require verification by qualified medical professionals.
        </p>
      </div>

      <main className="main-content">
        <div className="content-grid">
          <div className="input-panel">
            
            <section className="panel-section model-section">
              <div className="section-header">
                <div className="section-title">
                  <Cpu size={20} />
                  <span>AI Opinions</span>
                  {!isSuperAdmin && (
                    <span className="credits-needed">({selectedModels.length} credit{selectedModels.length !== 1 ? 's' : ''})</span>
                  )}
                </div>
              </div>
              <div className="section-content">
                <div className="model-grid">
                  {AI_MODELS.map(model => (
                    <div key={model.id} className="model-card-wrapper">
                      <button
                        className={'model-card ' + (selectedModels.includes(model.id) ? 'selected' : '')}
                        onClick={() => toggleModel(model.id)}
                        style={{ '--model-color': model.color }}
                      >
                        <div className="model-checkbox">
                          {selectedModels.includes(model.id) && <CheckCircle size={16} />}
                        </div>
                        <div className="model-info">
                          <span className="model-name">{model.name}</span>
                          <span className="model-provider">{model.provider}</span>
                          <span className="model-desc">{model.description}</span>
                        </div>
                      </button>
                      <button 
                        className="model-chat-btn"
                        onClick={() => setChatMode(model.id)}
                        style={{ background: model.color }}
                        title={'Chat with ' + model.provider}
                      >
                        <MessageCircle size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="model-hint">
                  <Zap size={14} />
                  Select for quick analysis or click <MessageCircle size={12} /> for interactive chat
                </p>
              </div>
            </section>

            <section className="panel-section">
              <button 
                className="section-header collapsible"
                onClick={() => setShowPatientInfo(!showPatientInfo)}
              >
                <div className="section-title">
                  <User size={20} />
                  <span>Patient Information</span>
                  <span className="optional-badge">Optional</span>
                </div>
                {showPatientInfo ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              
              {showPatientInfo && (
                <div className="section-content">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Age</label>
                      <input
                        type="text"
                        placeholder="e.g., 45"
                        value={patientInfo.age}
                        onChange={(e) => setPatientInfo(prev => ({ ...prev, age: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Gender</label>
                      <select
                        value={patientInfo.gender}
                        onChange={(e) => setPatientInfo(prev => ({ ...prev, gender: e.target.value }))}
                      >
                        <option value="">Select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Weight (kg)</label>
                      <input
                        type="text"
                        placeholder="e.g., 70"
                        value={patientInfo.weight}
                        onChange={(e) => setPatientInfo(prev => ({ ...prev, weight: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Height (cm)</label>
                      <input
                        type="text"
                        placeholder="e.g., 175"
                        value={patientInfo.height}
                        onChange={(e) => setPatientInfo(prev => ({ ...prev, height: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Blood Type</label>
                      <select
                        value={patientInfo.bloodType}
                        onChange={(e) => setPatientInfo(prev => ({ ...prev, bloodType: e.target.value }))}
                      >
                        <option value="">Select...</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Allergies</label>
                      <input
                        type="text"
                        placeholder="e.g., Penicillin"
                        value={patientInfo.allergies}
                        onChange={(e) => setPatientInfo(prev => ({ ...prev, allergies: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="form-group full-width">
                    <label>Medical History</label>
                    <textarea
                      placeholder="Previous conditions, surgeries, medications..."
                      value={medicalHistory}
                      onChange={(e) => setMedicalHistory(e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </section>

            <section className="panel-section">
              <div className="section-header">
                <div className="section-title">
                  <Activity size={20} />
                  <span>Symptoms</span>
                </div>
              </div>
              
              <div className="section-content">
                <div className="input-mode-tabs">
                  {INPUT_MODES.map(mode => {
                    const Icon = mode.icon;
                    return (
                      <button
                        key={mode.id}
                        className={'mode-tab ' + (inputMode === mode.id ? 'active' : '')}
                        onClick={() => setInputMode(mode.id)}
                      >
                        <Icon size={18} />
                        <span>{mode.label}</span>
                      </button>
                    );
                  })}
                </div>

                {inputMode === 'text' && (
                  <div className="input-area">
                    <textarea
                      className="symptoms-textarea"
                      placeholder="Describe the patient's symptoms in detail..."
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                      rows={6}
                    />
                  </div>
                )}

                {inputMode === 'audio' && (
                  <div className="input-area recording-area">
                    <div className="recording-controls">
                      {!isRecording && !audioBlob && (
                        <button className="record-btn" onClick={startAudioRecording}>
                          <Mic size={24} />
                          <span>Start Recording</span>
                        </button>
                      )}
                      
                      {isRecording && (
                        <div className="recording-active">
                          <div className="recording-indicator">
                            <span className="pulse"></span>
                            <span>Recording...</span>
                          </div>
                          <div className="recording-time">{formatTime(recordingTime)}</div>
                          <button className="stop-btn" onClick={stopRecording}>
                            <MicOff size={20} />
                            <span>Stop</span>
                          </button>
                        </div>
                      )}

                      {audioBlob && !isRecording && (
                        <div className="recording-preview">
                          <audio controls src={URL.createObjectURL(audioBlob)} />
                          <button className="btn-secondary btn-small" onClick={() => setAudioBlob(null)}>
                            <RefreshCw size={16} />
                            Re-record
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="additional-notes">
                      <label>Additional Notes (Optional)</label>
                      <textarea
                        placeholder="Add any additional context..."
                        value={symptoms}
                        onChange={(e) => setSymptoms(e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {inputMode === 'video' && (
                  <div className="input-area recording-area">
                    <div className="video-container">
                      {(isRecording || !videoBlob) && (
                        <video
                          ref={videoPreviewRef}
                          autoPlay
                          muted
                          playsInline
                          className="video-preview"
                        />
                      )}
                      
                      {videoPreviewUrl && !isRecording && (
                        <video
                          controls
                          src={videoPreviewUrl}
                          className="video-preview"
                        />
                      )}

                      {!isRecording && !videoBlob && (
                        <div className="video-overlay">
                          <button className="record-btn" onClick={startVideoRecording}>
                            <Video size={24} />
                            <span>Start Recording</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {isRecording && (
                      <div className="recording-active">
                        <div className="recording-indicator">
                          <span className="pulse"></span>
                          <span>Recording...</span>
                        </div>
                        <div className="recording-time">{formatTime(recordingTime)}</div>
                        <button className="stop-btn" onClick={stopRecording}>
                          <VideoOff size={20} />
                          <span>Stop</span>
                        </button>
                      </div>
                    )}

                    {videoBlob && !isRecording && (
                      <button className="btn-secondary btn-small" onClick={() => {
                        setVideoBlob(null);
                        setVideoPreviewUrl(null);
                      }}>
                        <RefreshCw size={16} />
                        Re-record
                      </button>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="panel-section">
              <div className="section-header">
                <div className="section-title">
                  <Upload size={20} />
                  <span>Files</span>
                  <span className="optional-badge">Optional</span>
                </div>
              </div>
              
              <div className="section-content">
                <div
                  className="file-drop-zone"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,audio/*,video/*,.pdf"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <div className="drop-zone-content">
                    <div className="drop-zone-icons">
                      <Image size={20} />
                      <FileAudio size={20} />
                      <FileVideo size={20} />
                    </div>
                    <p>Tap to upload files</p>
                    <span>X-Rays, Scans, Reports</span>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="uploaded-files">
                    {uploadedFiles.map(file => {
                      const FileIcon = getFileIcon(file.type);
                      return (
                        <div key={file.id} className="file-item">
                          <div className="file-preview">
                            {file.preview ? (
                              <img src={file.preview} alt={file.name} />
                            ) : (
                              <FileIcon size={24} />
                            )}
                          </div>
                          <div className="file-info">
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">{formatFileSize(file.size)}</span>
                          </div>
                          <button className="remove-file" onClick={() => removeFile(file.id)}>
                            <X size={18} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <div className="submit-area">
              <button
                className="btn-primary submit-btn"
                onClick={submitDiagnosis}
                disabled={!canSubmit()}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="spin" />
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Get AI Opinions</span>
                  </>
                )}
              </button>
              
              <button className="btn-secondary btn-icon" onClick={resetForm} title="Clear All">
                <Trash2 size={20} />
              </button>
            </div>
          </div>

          <div className="results-panel">
            <div className="panel-section results-section">
              <div className="section-header">
                <div className="section-title">
                  <ClipboardList size={20} />
                  <span>AI Opinions</span>
                </div>
              </div>

              <div className="section-content results-content">
                {!diagnosisResults && !loading && !error && (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <Eye size={40} />
                    </div>
                    <h3>Ready for Analysis</h3>
                    <p>Select AI opinions, enter symptoms, and click "Get AI Opinions"</p>
                  </div>
                )}

                {loading && (
                  <div className="loading-state">
                    <div className="loading-animation">
                      <Heart size={40} className="pulse-heart" />
                    </div>
                    <h3>Analyzing...</h3>
                    <p>Getting {selectedModels.length} AI opinion{selectedModels.length > 1 ? 's' : ''}</p>
                    <div className="loading-models">
                      {selectedModels.map(modelId => {
                        const model = AI_MODELS.find(m => m.id === modelId);
                        return (
                          <span key={modelId} className="loading-model-badge" style={{ borderColor: model?.color }}>
                            <Loader2 size={12} className="spin" />
                            {model?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {error && (
                  <div className="error-state">
                    <XCircle size={40} />
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button className="btn-secondary" onClick={() => setError(null)}>
                      <RefreshCw size={16} />
                      Try Again
                    </button>
                  </div>
                )}

                {transcription && (
                  <div className="transcription-box">
                    <h4>Transcription</h4>
                    <p>{transcription}</p>
                  </div>
                )}

                {consensus && diagnosisResults && (
                  <div className={'consensus-box consensus-' + consensus.consensusLevel}>
                    <div className="consensus-header">
                      <CheckCircle size={18} />
                      <span>
                        {consensus.consensusLevel === 'full' && 'All Opinions Received'}
                        {consensus.consensusLevel === 'partial' && 'Partial Response'}
                        {consensus.consensusLevel === 'single' && 'Single Opinion'}
                        {consensus.consensusLevel === 'none' && 'No Response'}
                      </span>
                    </div>
                    <p>{consensus.summary}</p>
                  </div>
                )}

                {diagnosisResults && diagnosisResults.length > 0 && (
                  <div className="diagnosis-results">
                    <div className="result-tabs">
                      {diagnosisResults.map((result, index) => {
                        const model = AI_MODELS.find(m => m.name === result.model);
                        return (
                          <button
                            key={index}
                            className={'result-tab ' + (activeResultTab === index ? 'active' : '') + ' ' + (result.success ? '' : 'error')}
                            onClick={() => setActiveResultTab(index)}
                            style={{ '--tab-color': model?.color || '#666' }}
                          >
                            {result.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                            <span>{result.model}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="result-content">
                      {diagnosisResults[activeResultTab]?.success ? (
                        <>
                          <div className="result-header">
                            <span className="result-provider">
                              {diagnosisResults[activeResultTab].model}
                            </span>
                            <span className="result-time">
                              {new Date().toLocaleTimeString()}
                            </span>
                          </div>
                          <div className="diagnosis-text">
                            {renderDiagnosisContent(diagnosisResults[activeResultTab].diagnosis)}
                          </div>
                        </>
                      ) : (
                        <div className="result-error">
                          <XCircle size={24} />
                          <p>This opinion could not be generated.</p>
                          <span>{diagnosisResults[activeResultTab]?.error}</span>
                        </div>
                      )}
                    </div>

                    <div className="result-actions">
                      <button 
                        className="btn-secondary"
                        onClick={() => setShowReferences(!showReferences)}
                      >
                        <BookOpen size={16} />
                        {showReferences ? 'Hide' : 'Show'} References
                      </button>
                      <button 
                        className="btn-secondary"
                        onClick={() => {
                          const allDiagnoses = diagnosisResults
                            .filter(r => r.success)
                            .map(r => '=== ' + r.model + ' ===\n\n' + r.diagnosis)
                            .join('\n\n' + '='.repeat(50) + '\n\n');
                          
                          const referencesText = references.length > 0 
                            ? '\n\n' + '='.repeat(50) + '\n\n=== REFERENCES ===\n\n' + 
                              references.map((ref, i) => 
                                (i + 1) + '. [' + ref.type + '] ' + ref.title + '\n   Authors: ' + ref.authors + '\n   Source: ' + ref.source + ' (' + ref.year + ')\n   Relevance: ' + ref.relevance + '\n   ' + (ref.doi ? 'Link: ' + ref.doi : '')
                              ).join('\n\n')
                            : '';
                          
                          const blob = new Blob([allDiagnoses + referencesText], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'diagnosis-' + new Date().toISOString().split('T')[0] + '.txt';
                          a.click();
                        }}
                      >
                        <Download size={16} />
                        Export All
                      </button>
                    </div>
                  </div>
                )}

                {showReferences && references.length > 0 && (
                  <div className="references-section">
                    <div className="references-header">
                      <FileSearch size={20} />
                      <span>Evidence & References</span>
                    </div>
                    <div className="references-list">
                      {references.map((ref, index) => (
                        <div key={index} className="reference-card">
                          <div className="reference-type-badge" data-type={ref.type?.toLowerCase()}>
                            {ref.type}
                          </div>
                          <div className="reference-content">
                            <h4 className="reference-title">{ref.title}</h4>
                            <p className="reference-authors">{ref.authors}</p>
                            <p className="reference-source">
                              {ref.source} • {ref.year}
                            </p>
                            <p className="reference-relevance">
                              <strong>Relevance:</strong> {ref.relevance}
                            </p>
                            {ref.doi && (
                              <a 
                                href={ref.doi.startsWith('http') ? ref.doi : 'https://doi.org/' + ref.doi}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="reference-link"
                              >
                                <ExternalLink size={14} />
                                View Source
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="references-disclaimer">
                      <AlertCircle size={14} />
                      References are AI-generated suggestions. Verify all sources before clinical use.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <p>
          <strong>LatDiagnostics</strong> — AI suggestions require professional medical verification.
        </p>
        <p>© 2024 LatDiagnostics. Made in Maharashtra, India.</p>
      </footer>
    </div>
  );
}

function AppWrapper() {
  const [authView, setAuthView] = useState('login');
  const { currentUser } = useAuth();

  if (!currentUser) {
    if (authView === 'signup') {
      return <Signup onSwitchToLogin={() => setAuthView('login')} />;
    }
    if (authView === 'forgot') {
      return <ForgotPassword onBackToLogin={() => setAuthView('login')} />;
    }
    return (
      <Login 
        onSwitchToSignup={() => setAuthView('signup')} 
        onForgotPassword={() => setAuthView('forgot')}
      />
    );
  }

  return <DiagnosisApp />;
}

function App() {
  return (
    <AuthProvider>
      <AppWrapper />
      <InstallPrompt />
    </AuthProvider>
  );
}

export default App;
